<template lang="pug">
yz-card(color="white")
  .pa-6
    .yz-heading-8.mb-5 {{ $t('panel.logs') }}

    // Initial loading state
    template(v-if="isFetching")
      .log-entries
        div(v-for="i in 4" :key="i")
          yz-card.pa-3.mb-3(outlined)
            .d-flex.align-start
              yz-skeleton-loader.mr-3(type="avatar" width="24px" height="24px")
              .flex-grow-1
                yz-skeleton-loader.mb-2(type="text" width="70%")
                yz-skeleton-loader(type="text" width="40%")

    // Error state
    template(v-else-if="isError")
      yz-empty-state-card(icon="$yzi-alert-triangle" icon-color="yz-danger-500" border-color="yz-danger-500" :title="$t('panel.unable_to_load_logs')" :description="$t('panel.log_info_could_not_be_retrieved')" :action-label="$t('panel.try_again')" action-size="sm" @action-click="fetchLogs")

    // Content
    template(v-else)
      // Loading indicator for refetch
      yz-progress-linear.mt-n5.mb-5(v-if="isLoading" indeterminate color="primary")

      // Log entries
      .log-entries
        div(v-for="(log, index) in logEntries" :key="log.id")
          // Log entry card
          yz-card.pa-3(outlined)
            .d-flex.align-start
              // Icon container with rounded background
              .mr-3.d-flex.align-center.justify-center.flex-shrink-0(style="width: 24px; height: 24px; background-color: var(--yz-neutral-300); border-radius: 12px;")
                yz-icon(size="14px" color="yz-neutral-500") {{ getLogIcon(log.tag) }}

              // Content
              .flex-grow-1
                .d-flex.align-center.justify-space-between
                  .yz-regular-body-sm {{ log.activity }}
                  yz-btn(v-if="!log.hasNote || !log.note" :label="$t('panel.add_note')" size="sm" outlined background-color="white" @click="onAddNote(log)")

                .yz-regular-body-xs.text-neutral-500.mb-3 {{ log.date }}

                // Existing note
                yz-card.pa-3.mb-3(v-if="log.hasNote && log.note" color="yz-neutral-200")
                  .d-flex.justify-space-between.align-center.mb-2
                    .yz-heading-10 {{ $t('note') }}
                    yz-menu(offset-y attach left)
                      template(v-slot:activator="{ on, attrs }")
                        yz-btn(v-bind="attrs" v-on="on" icon="$yzi-dots-horizontal" size="sm" outlined background-color="white")
                      yz-list
                        yz-list-item(@click="onEditNote(log)")
                          div {{ $t('edit') }}
                        yz-divider(color="yz-neutral-300")
                        yz-list-item(@click="onDeleteNote(log)")
                          .text-danger-500 {{ $t('delete') }}
                  .yz-regular-body-xs.text-neutral-700 {{ log.note }}
                  .d-flex.align-center.mt-1(v-if="log.updatedBy || log.updatedAt")
                    yz-icon.mr-1(size="14px" color="yz-neutral-500") $yzi-user-edit
                    .yz-medium-body-2xs.text-neutral-500
                      span(v-if="log.updatedBy") {{ log.updatedBy }}
                      span(v-if="log.updatedAt") ,&nbsp; {{ log.updatedAt }}


          // Vertical divider connector (except for last entry)
          .d-flex.justify-start.my-1.ml-7(v-if="index < logEntries.length - 1")
            yz-divider(vertical style="height:16px" width="2px")

      // Pagination
      .d-flex.justify-end.pt-3
        yz-pagination(v-model="currentPage" :page-size.sync="pageSize" :count="totalCount" arrow, type="item" @input="fetchLogs")

      // Add Log Note Dialog
      add-log-note-dialog(v-if="showAddLogNoteDialog" v-model="showAddLogNoteDialog" :log-id="selectedLogData.id" :activity="selectedLogData.activity" :date="selectedLogData.date" :icon="selectedLogData.icon" :parent-note="selectedLogData.note" @updated="onNoteUpdated" @canceled="onNoteCanceled")

      // Delete Log Note Confirmation Dialog
      yz-delete-confirmation-dialog(v-if="showDeleteLogNoteDialog" v-model="showDeleteLogNoteDialog" item="note" :loading="isDeletingLogNote" @confirmed="onConfirmDeleteNote" @canceled="onCancelDeleteNote")
        // Note preview
        yz-card.pa-3.mt-4(v-if="logNoteToDelete" color="yz-neutral-200")
          .yz-heading-10.mb-2 {{ $t('note') }}
          .yz-regular-body-xs.text-neutral-700 {{ logNoteToDelete.note }}
          .d-flex.align-center.mt-1(v-if="logNoteToDelete.updatedBy || logNoteToDelete.updatedAt")
            yz-icon.mr-1(size="14px" color="yz-neutral-500") $yzi-user-edit
            .yz-medium-body-2xs.text-neutral-500
              span(v-if="logNoteToDelete.updatedBy") {{ logNoteToDelete.updatedBy }}
              span(v-if="logNoteToDelete.updatedAt") ,&nbsp; {{ logNoteToDelete.updatedAt }}
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { DateTime } from 'luxon'
import AddLogNoteDialog from '@/components/clinic/panel/dialogs/AddLogNoteDialog.vue'

export default {
  components: { AddLogNoteDialog },
  props: {
    parentClaim: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      // Loading states
      isFetching: true,
      isLoading: false,
      isError: false,
      initialized: false,
      // Pagination
      currentPage: 1,
      pageSize: 10,
      totalCount: 0,
      // Data
      logEntries: [],
      showAddLogNoteDialog: false,
      selectedLogData: null,
      showDeleteLogNoteDialog: false,
      logNoteToDelete: null,
      isDeletingLogNote: false
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany'
    })
  },
  mounted() {
    this.fetchLogs()
  },
  methods: {
    ...mapActions(['showMessage']),
    async fetchLogs() {
      if (!this.parentClaim?.id) {
        this.isError = true
        this.isFetching = false
        return
      }

      // Only show page-level loading on initial load
      if (!this.initialized) {
        this.isFetching = true
      } else {
        this.isLoading = true
      }
      this.isError = false

      try {
        const params = {
          company: this.company.id,
          app_model: 'panel.claim',
          object_id: this.parentClaim.id,
          page: this.currentPage,
          page_size: this.pageSize
        }

        const { data } = await this.$axios.get('/actions', { params })

        // Transform API response to match component structure
        this.logEntries = data.results.map(log => ({
          id: log.id,
          tag: log.tag,
          activity: log.activity,
          date: this.formatLogDate(log.created),
          note: log.note || null,
          hasNote: log.note !== null && log.note !== '',
          updatedBy: log.note_by ? `${log.note_by.first_name} ${log.note_by.last_name}` : null,
          updatedAt: log.note_at ? this.formatLogDate(log.note_at) : null
        }))

        this.totalCount = data.count
        this.initialized = true
      } catch (error) {
        console.error('Error fetching logs:', error)
        this.isError = true
        await this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isFetching = false
        this.isLoading = false
      }
    },
    formatLogDate(isoString) {
      try {
        const date = DateTime.fromISO(isoString)
        return date.toFormat('dd MMM yyyy, h:mma').toLowerCase()
      } catch (error) {
        console.error('Error formatting date:', error)
        return isoString
      }
    },
    onAddNote(log) {
      this.selectedLogData = {
        id: log.id,
        activity: log.activity,
        date: log.date,
        icon: this.getLogIcon(log.tag),
      }
      this.showAddLogNoteDialog = true
    },
    onNoteUpdated() {
      this.showAddLogNoteDialog = false
      this.selectedLogData = null
      this.fetchLogs()
    },
    onNoteCanceled() {
      this.showAddLogNoteDialog = false
      this.selectedLogData = null
    },
    getLogIcon(tag) {
      const iconMap = {
        'invoice_removed': '$yzi-x-sm',
        'status_updated': '$yzi-repeat-04',
        'status_reverted': '$yzi-reverse-left',
        'edited': '$yzi-edit-01',
        'created': '$yzi-receipt'
      }
      return iconMap[tag] || ''
    },
    onEditNote(log) {
      this.selectedLogData = {
        id: log.id,
        activity: log.activity,
        date: log.date,
        icon: this.getLogIcon(log.tag),
        note: log.note
      }
      this.showAddLogNoteDialog = true
    },
    onDeleteNote(log) {
      this.logNoteToDelete = log
      this.showDeleteLogNoteDialog = true
    },
    async onConfirmDeleteNote() {
      if (!this.logNoteToDelete) return

      this.isDeletingLogNote = true
      try {
        await this.$axios.patch(`/actions/${this.logNoteToDelete.id}`, {
          note: null
        })

        this.showDeleteLogNoteDialog = false
        this.logNoteToDelete = null
        // Refresh logs from backend
        await this.fetchLogs()
        await this.showMessage(['success', this.$t('panel.note_deleted_successfully')])
      } catch (error) {
        console.error('Error deleting note:', error)
        await this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isDeletingLogNote = false
      }
    },
    onCancelDeleteNote() {
      this.showDeleteLogNoteDialog = false
      this.logNoteToDelete = null
    }
  }
}
</script>

<style scoped></style>
